import { useEffect, useState } from "react";
import { Pencil, Archive, Trash2, RotateCcw, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError, GRADES, gradeLabel } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AvatarDesigner } from "@/components/live/AvatarDesigner";
import { KidAvatar } from "@/components/live/KidAvatar";
import { KidLoginSetup } from "@/components/kids/KidLoginSetup";
import { SpanishDefault } from "@/components/SpanishDefault";

const input = "w-full px-4 py-2.5 rounded-xl bg-white border border-[#E6DEC3] text-sm focus:outline-none focus:border-[#2C4A3E]";
const pill = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors";
const billingNote = (b) => b?.error ? toast.warning(b.error) : b?.seats && toast.success(`Plan updated to ${b.seats} learner${b.seats > 1 ? "s" : ""} from your next bill`);

export const EditChildDialog = ({ k, open, setOpen, onDone }) => {
  const [f, setF] = useState({ name: k.name, grade: k.grade || "5", state: k.state || "ga" });
  const [avatar, setAvatar] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF({ name: k.name, grade: k.grade || "5", state: k.state || "ga" }); setAvatar(null); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (key) => (e) => setF({ ...f, [key]: e.target.value });
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await http.put(`/family/children/${k.id}`, { ...f, ...(avatar ? { avatar } : {}) }); toast.success(`${f.name}'s profile updated`); setOpen(false); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#FDFBF7] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`edit-child-dialog-${k.id}`}>
        <DialogTitle className="font-display text-2xl text-[#2C4A3E]">Edit {k.name.split(" ")[0]}'s profile</DialogTitle>
        <form onSubmit={save} className="space-y-4">
          <input required maxLength={60} value={f.name} onChange={set("name")} placeholder="Child's name" data-testid="edit-child-name" className={input} />
          <div className="grid grid-cols-2 gap-3">
            <select value={f.grade} onChange={set("grade")} data-testid="edit-child-grade" className={input}>{GRADES.map((g) => <option key={g} value={g}>{gradeLabel(g)}</option>)}</select>
            <select value={f.state} onChange={set("state")} disabled={!!k.edition_source} title={k.edition_source ? `Set by ${k.edition_source.by} for ${k.edition_source.class}` : undefined} data-testid="edit-child-state" className={`${input} disabled:opacity-60`}><option value="ga">Georgia</option><option value="fl">Florida</option><option value="al">Alabama</option></select>
          </div>
          {k.edition_source && <p className="text-xs text-[#7A4A12]" data-testid="edit-child-edition-locked">Edition is set by {k.edition_source.by} for {k.edition_source.class}.</p>}
          <details className="rounded-2xl border border-[#E6DEC3] bg-white/60 p-4" data-testid="edit-child-avatar-section">
            <summary className="cursor-pointer text-sm text-[#2C4A3E]">Change avatar</summary>
            <div className="mt-4"><AvatarDesigner initial={k.avatar} onPick={setAvatar} hideSave /></div>
          </details>
          <button disabled={busy} data-testid="edit-child-save" className="w-full py-3 rounded-full bg-[#2C4A3E] text-white disabled:opacity-60">{busy ? "Saving…" : "Save changes"}</button>
        </form>
        <SpanishDefault sid={k.id} name={k.name.split(" ")[0]} />
        <div className="rounded-2xl border border-[#E6DEC3] bg-white/60 p-4 flex flex-wrap items-center gap-3 text-sm text-[#2C4A3E]">
            <KeyRound className="w-4 h-4" /> Picture password <KidLoginSetup kid={k} onChange={onDone} idSuffix="-edit" />
        </div>

      </DialogContent>
    </Dialog>
  );
};

export const RemoveChildDialog = ({ k, open, setOpen, onDone, permanentOnly }) => {
  const [mode, setMode] = useState(permanentOnly ? "delete" : "archive");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTyped(""); setMode(permanentOnly ? "delete" : "archive"); } }, [open, permanentOnly]);
  const ok = typed.trim().toLowerCase() === k.name.trim().toLowerCase();
  const go = async () => {
    setBusy(true);
    try {
      const r = await http.post(`/family/children/${k.id}/${mode}`, { confirm_name: typed });
      toast.success(mode === "archive" ? `${k.name} archived. You can restore within 30 days.` : r.data.result === "unlinked" ? `${k.name} was unlinked from your family` : `${k.name} was permanently deleted`);
      billingNote(r.data.billing); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#FDFBF7]" data-testid={`remove-child-dialog-${k.id}`}>
        <DialogTitle className="font-display text-2xl text-[#2C4A3E]">Remove {k.name}?</DialogTitle>
        {!permanentOnly && <div className="grid gap-2">
          {[["archive", "Archive (recommended)", "Hides this profile and turns off their login. Restore anytime in the next 30 days."],
            ["delete", "Delete permanently", "Only if added by mistake. Erases the profile and all progress. Can't be undone."]].map(([m, t, d]) => (
            <button key={m} type="button" onClick={() => setMode(m)} data-testid={`remove-mode-${m}`}
              className={`text-left rounded-2xl border p-4 transition-colors ${mode === m ? (m === "delete" ? "border-red-400 bg-red-50" : "border-[#2C4A3E] bg-[#F1EADB]") : "border-[#E6DEC3] bg-white"}`}>
              <p className="text-sm font-semibold text-[#2C4A3E]">{t}</p><p className="text-xs text-stone-600 mt-1">{d}</p></button>))}
        </div>}
        {permanentOnly && <p className="text-sm text-stone-600">This erases the profile and all progress. It can't be undone.</p>}
        <label className="text-sm text-stone-600">Type <b className="text-[#2C4A3E]">{k.name}</b> to confirm
          <input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="remove-child-confirm-input" className={`${input} mt-2`} autoComplete="off" /></label>
        <p className="text-xs text-stone-500">On a family plan, your learner count goes down from your next bill.</p>
        <button disabled={!ok || busy} onClick={go} data-testid="remove-child-submit"
          className={`w-full py-3 rounded-full text-white disabled:opacity-40 ${mode === "delete" ? "bg-red-700" : "bg-[#C87D53]"}`}>
          {busy ? "Working…" : mode === "delete" ? "Delete permanently" : "Archive profile"}</button>
      </DialogContent>
    </Dialog>
  );
};

export const ManageChildButtons = ({ k, onChange }) => {
  const [edit, setEdit] = useState(false);
  const [remove, setRemove] = useState(false);
  return (
    <div className="flex gap-2">
      <button onClick={() => setEdit(true)} data-testid={`parent-edit-child-${k.id}`} className={`${pill} border-[#E6DEC3] bg-white text-[#2C4A3E] hover:bg-[#F1EADB]`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
      <button onClick={() => setRemove(true)} data-testid={`parent-remove-child-${k.id}`} className={`${pill} border-red-200 bg-white text-red-700 hover:bg-red-50`}><Archive className="w-3.5 h-3.5" /> Remove</button>
      <EditChildDialog k={k} open={edit} setOpen={setEdit} onDone={onChange} />
      <RemoveChildDialog k={k} open={remove} setOpen={setRemove} onDone={onChange} />
    </div>
  );
};

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

const ArchivedRow = ({ k, onChange }) => {
  const [del, setDel] = useState(false);
  const restore = async () => {
    try { const r = await http.post(`/family/children/${k.id}/restore`); toast.success(`${k.name} is back`); billingNote(r.data.billing); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <li className="rounded-2xl bg-white/70 border border-[#E6DEC3] p-3" data-testid={`archived-child-${k.id}`}>
      <div className="flex items-center gap-3"><KidAvatar a={k.avatar} className="w-10 h-10 opacity-70 shrink-0" />
      <div className="min-w-0"><p className="text-sm text-[#2C4A3E] break-words">{k.name}</p><p className="text-xs text-stone-500" data-testid={`archived-days-${k.id}`}>Deleted in {daysLeft(k.purge_after)} days</p></div></div>
      <div className="mt-3 flex gap-2"><button onClick={restore} data-testid={`restore-child-${k.id}`} className={`${pill} border-[#2C4A3E] text-[#2C4A3E] bg-white hover:bg-[#F1EADB]`}><RotateCcw className="w-3.5 h-3.5" /> Restore</button>
      <button onClick={() => setDel(true)} aria-label="Delete permanently" data-testid={`delete-archived-${k.id}`} className={`${pill} border-red-200 text-red-700 bg-white hover:bg-red-50`}><Trash2 className="w-3.5 h-3.5" /> Delete</button></div>
      <RemoveChildDialog k={k} open={del} setOpen={setDel} onDone={onChange} permanentOnly />
    </li>
  );
};

export const ArchivedKids = ({ refreshKey, onChange }) => {
  const [list, setList] = useState([]);
  const load = () => http.get("/family/archived").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);
  if (!list.length) return null;
  const changed = () => { load(); onChange(); };
  return (
    <section className="rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] p-6" data-testid="archived-kids">
      <p className="font-display text-xl text-[#2C4A3E]">Archived profiles</p>
      <p className="text-xs text-stone-500 mt-1 mb-4">Logins are off. Restore within 30 days or they're deleted for good.</p>
      <ul className="space-y-2">{list.map((k) => <ArchivedRow key={k.id} k={k} onChange={changed} />)}</ul>
    </section>
  );
};
